import React from 'react';
import {
  AdminSubscriptionPlan,
  AdminTranscriptionStatus,
  EmailStatus,
  EmailTrigger,
  EmailType,
  SignupStatus,
} from '../../domain/admin';
import {VIDEO_EDITOR_LABELS, VideoEditor} from '../../backend-types/user';
import {AlertTriangleIcon, CheckIcon, XCircleIcon} from '../Icons';

/**
 * Every badge in the admin panel, in one file, because most of them encode a distinction that is
 * easy to lose in a hand-written class string somewhere:
 *
 *  - **Subscription** badges off the *derived* fields. A lapsed Pro still carries
 *    `subscription_plan: 'pro'` in the Paddle mirror; rendering that is the bug the two-field
 *    contract exists to prevent, so the raw plan is not accepted by this component at all.
 *  - **`sent` is not `delivered`.** Resend accepting a message says nothing about it arriving, so
 *    `sent` reads "Accepted by provider" in neutral grey and only `delivered` earns a green check.
 *  - **`rejected`** on a transcription means the client under-reported the duration and the
 *    transcript was withheld — the seconds were charged anyway, which is why it is amber (a
 *    billing question to answer) rather than red (a system fault).
 *
 * Colour is semantic only, per the app's visual language: emerald = good, amber = needs a look,
 * red = failed, grey = neutral or unknown. Indigo is the accent and is not a status.
 */

type Tone = 'neutral' | 'good' | 'warn' | 'bad' | 'accent';

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'border-gray-700 bg-gray-800/60 text-gray-300',
  good: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  warn: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  bad: 'border-red-500/40 bg-red-500/10 text-red-300',
  accent: 'border-indigo-500/40 bg-indigo-500/10 text-indigo-300',
};

interface BadgeProps {
  tone?: Tone;
  title?: string;
  children: React.ReactNode;
  className?: string;
}

export const Badge: React.FC<BadgeProps> = ({tone = 'neutral', title, children, className = ''}) => (
  <span
    title={title}
    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5
     text-[11px] font-medium ${TONE_CLASS[tone]} ${className}`}
  >
    {children}
  </span>
);

const PLAN_LABEL: Record<AdminSubscriptionPlan, string> = {
  free: 'Free',
  pro: 'Pro',
  team: 'Team',
};

interface SubscriptionBadgeProps {
  /** Derived server-side. Never pass `subscription_plan` from the Paddle mirror. */
  effectivePlan: AdminSubscriptionPlan;
  isActive: boolean;
  /** The raw mirror, for the tooltip only — shown when the two disagree. */
  rawPlan?: AdminSubscriptionPlan;
}

export const SubscriptionBadge: React.FC<SubscriptionBadgeProps> = ({
  effectivePlan,
  isActive,
  rawPlan,
}) => {
  const disagrees = rawPlan !== undefined && rawPlan !== effectivePlan;
  const title = disagrees
    ? `Paddle mirror still says ${PLAN_LABEL[rawPlan]} — the derived value is what the app enforces`
    : undefined;

  if (!isActive) {
    return (
      <Badge tone="neutral" title={title}>
        {effectivePlan === 'free' ? 'Free' : `${PLAN_LABEL[effectivePlan]} (inactive)`}
      </Badge>
    );
  }

  return (
    <Badge tone={effectivePlan === 'free' ? 'neutral' : 'good'} title={title}>
      {PLAN_LABEL[effectivePlan]}
    </Badge>
  );
};

/* ==========================================================================
 * Email delivery
 * ========================================================================== */

interface StatusMeta {
  label: string;
  tone: Tone;
  title: string;
}

const EMAIL_STATUS: Record<EmailStatus, StatusMeta> = {
  sent: {
    label: 'Accepted by provider',
    tone: 'neutral',
    title: 'Resend accepted the message. That is not proof it arrived — only Delivered is.',
  },
  send_failed: {
    label: 'Send failed',
    tone: 'bad',
    title: 'Our API call to the email provider failed. Nothing was sent.',
  },
  delivered: {
    label: 'Delivered',
    tone: 'good',
    title: 'The provider confirmed delivery to the recipient’s mail server.',
  },
  delivery_delayed: {
    label: 'Delayed',
    tone: 'warn',
    title: 'The provider is still retrying. It may still arrive.',
  },
  bounced: {
    label: 'Bounced',
    tone: 'bad',
    title: 'The recipient’s mail server rejected it. They will never receive this email.',
  },
  complained: {
    label: 'Marked as spam',
    tone: 'bad',
    title: 'The recipient marked the message as spam.',
  },
  suppressed: {
    label: 'Suppressed — not delivered',
    tone: 'bad',
    title:
      'Resend accepted the send and returned a message id, then dropped it because the address is '
      + 'on the account suppression list. It looked like a success and delivered nothing.',
  },
};

export const EmailStatusBadge: React.FC<{status: EmailStatus}> = ({status}) => {
  const meta = EMAIL_STATUS[status] ?? {label: status, tone: 'neutral' as Tone, title: ''};

  return (
    <Badge tone={meta.tone} title={meta.title}>
      {/* Only `delivered` gets the check. `sent` deliberately gets no glyph at all. */}
      {status === 'delivered' && <CheckIcon className="h-3 w-3" />}
      {(status === 'bounced'
        || status === 'send_failed'
        || status === 'complained'
        || status === 'suppressed') && <XCircleIcon className="h-3 w-3" />}
      {status === 'delivery_delayed' && <AlertTriangleIcon className="h-3 w-3" />}
      {meta.label}
    </Badge>
  );
};

const EMAIL_TYPE_LABEL: Record<EmailType, string> = {
  welcome: 'Welcome',
  login_link: 'Login link',
  password_changed: 'Password changed',
};

export const EmailTypeBadge: React.FC<{emailType: EmailType}> = ({emailType}) => (
  <Badge tone="neutral">{EMAIL_TYPE_LABEL[emailType] ?? emailType}</Badge>
);

const TRIGGER_LABEL: Record<EmailTrigger, string> = {
  system: 'Automatic',
  user: 'User',
  admin: 'Admin',
};

export const EmailTriggerLabel: React.FC<{triggeredBy: EmailTrigger}> = ({triggeredBy}) => (
  <span className="text-xs text-gray-500">{TRIGGER_LABEL[triggeredBy] ?? triggeredBy}</span>
);

/* ==========================================================================
 * Transcriptions
 * ========================================================================== */

const TRANSCRIPTION_STATUS: Record<AdminTranscriptionStatus, StatusMeta> = {
  processing: {label: 'Processing', tone: 'accent', title: 'Still running at AssemblyAI.'},
  completed: {label: 'Completed', tone: 'good', title: 'Finished; the CSV is available.'},
  failed: {label: 'Failed', tone: 'bad', title: 'The job failed. See the error.'},
  rejected: {
    label: 'Rejected',
    tone: 'warn',
    title:
      'The client under-reported the duration, so the transcript was withheld. '
      + 'The seconds were still charged.',
  },
};

export const TranscriptionStatusBadge: React.FC<{status: AdminTranscriptionStatus}> = ({status}) => {
  const meta = TRANSCRIPTION_STATUS[status] ?? {label: status, tone: 'neutral' as Tone, title: ''};

  return (
    <Badge tone={meta.tone} title={meta.title}>
      {status === 'processing' && (
        <span className="h-2.5 w-2.5 animate-spin rounded-full border-[1.5px] border-current border-t-transparent" />
      )}
      {meta.label}
    </Badge>
  );
};

/** A soft-deleted transcription is kept in the list because it still spent its seconds. */
export const DeletedBadge: React.FC = () => (
  <Badge tone="neutral" title="Deleted by the user. The seconds it used were still charged.">
    Deleted
  </Badge>
);

/* ==========================================================================
 * Conversions
 * ========================================================================== */

/**
 * `video_editor` on a conversion row is a SNAPSHOT taken at generation time, not the user's
 * current profile setting — the tooltip carries that distinction since the badge itself has no
 * room to.
 */
export const VideoEditorBadge: React.FC<{videoEditor: VideoEditor | null}> = ({videoEditor}) => {
  if (!videoEditor) {
    return <span className="text-gray-600">—</span>;
  }

  return (
    <Badge
      tone="neutral"
      title="The editor selected at the moment this conversion was generated — may differ from the user's current setting."
    >
      {VIDEO_EDITOR_LABELS[videoEditor]}
    </Badge>
  );
};

/**
 * A null `audio_path` means the XML's source clip has no name. Harmless in Premiere, which just
 * opens the clip offline — DaVinci Resolve instead hangs trying to relink an empty reference. Red
 * only when the snapshot editor is Resolve: that pairing is the exact thing support needs to spot,
 * everything else is amber (worth a look, not yet a known problem).
 */
export const UnverifiedMediaBadge: React.FC<{videoEditor: VideoEditor | null}> = ({videoEditor}) => {
  const isResolve = videoEditor === 'davinci_resolve';

  return (
    <Badge
      tone={isResolve ? 'bad' : 'warn'}
      title={
        isResolve
          ? 'No media reference recorded, and this conversion was generated for DaVinci Resolve — '
            + 'Resolve hangs trying to relink an empty reference on import.'
          : 'No media reference recorded on this conversion. Rows from before 2026-08-26 predate '
            + 'the column; a null on a newer row is unusual and worth a second look.'
      }
    >
      <AlertTriangleIcon className="h-3 w-3" />
      Unverified media reference
    </Badge>
  );
};

/* ==========================================================================
 * Signups
 * ========================================================================== */

const SIGNUP_STATUS: Record<SignupStatus, StatusMeta> = {
  pending: {
    label: 'Pending',
    tone: 'warn',
    title: 'Started, not yet provisioned. May simply not have completed checkout.',
  },
  provisioned: {
    label: 'Provisioned',
    tone: 'good',
    title: 'The account exists and a login link has been emailed.',
  },
  expired: {
    label: 'Expired',
    tone: 'neutral',
    title: 'The signup token expired before checkout completed. Nothing was charged.',
  },
  failed: {
    label: 'Failed',
    tone: 'bad',
    title: 'Paid, but provisioning failed — this person cannot sign in.',
  },
};

export const SignupStatusBadge: React.FC<{status: SignupStatus}> = ({status}) => {
  const meta = SIGNUP_STATUS[status] ?? {label: status, tone: 'neutral' as Tone, title: ''};

  return (
    <Badge tone={meta.tone} title={meta.title}>
      {status === 'failed' && <AlertTriangleIcon className="h-3 w-3" />}
      {meta.label}
    </Badge>
  );
};

/* ==========================================================================
 * Account flags
 * ========================================================================== */

/**
 * `password_set === false` means the account was provisioned by a checkout and has never chosen a
 * password. The emailed magic link is the only way in, which is exactly when resending it matters.
 */
export const NoPasswordBadge: React.FC = () => (
  <Badge
    tone="warn"
    title="Provisioned by checkout and has never set a password — the emailed login link is their only way in."
  >
    No password
  </Badge>
);

export const AdminBadge: React.FC = () => <Badge tone="accent">Admin</Badge>;

/* ==========================================================================
 * Suppressions
 * ========================================================================== */

const ORIGIN_LABEL: Record<'bounce' | 'complaint' | 'manual', string> = {
  bounce: 'Hard bounce',
  complaint: 'Spam complaint',
  manual: 'Added by hand',
};

/**
 * A suppressed address is not a failed send — it is every *future* send failing silently, which is
 * why it is red even though nothing has visibly gone wrong yet. A cleared one is history, so it
 * goes neutral rather than green: nothing here is good news, some of it is just over.
 */
export const SuppressionBadge: React.FC<{
  origin: 'bounce' | 'complaint' | 'manual' | null;
  removedAt: string | null;
}> = ({origin, removedAt}) => {
  if (removedAt) {
    return (
      <Badge tone="neutral" title="Cleared in the Resend dashboard. Sending works again.">
        Cleared
      </Badge>
    );
  }

  return (
    <Badge
      tone="bad"
      title={
        'Resend refuses to deliver to this address. It still accepts sends and returns a message '
        + 'id, so nothing looks wrong until you check. Clearing it is done in the Resend dashboard.'
      }
    >
      {origin ? ORIGIN_LABEL[origin] : 'Suppressed'}
    </Badge>
  );
};
